/**
 * Copyright (c) 2024, CYBRA Corp. All rights reserved.
 */
import { Injectable } from "@angular/core"
import { Url_Base } from 'app/rest-constants';
import { RestService } from "./rest-service";
import { ServiceInstance } from "app/models/service-instance";

/** UUID of the O'Neil Service Type created in Edgefinity.  */
export const Oneil_PortalServiceType_Uuid = "0192b9cc-506f-710c-af93-11a61dfafe71";

/** Service Property Key for the "Autoprocess" setting */
export const Oneil_ServicePropertyKey_Autoprocess = 'autoprocess';

/**
 * Service for O'Neill's customizations.
 */
@Injectable()
export class OneillService {

  /** Constructor */
  constructor(
    private restService: RestService
  ) { }

  /**
   * Calls the "Get State" API for the given Portal ID.
   * Returns the current state informaton for the Portal.
   */
  public getState(portalId: number | string): Promise<Oneill_GetStateDto> {
    const url = `${Url_Base}/oneill/carton-portal-validation/${portalId}/get-state`;
    return this.restService.get<Oneill_GetStateDto>(url);
  }

  /**
   * Calls the "Enter Carton ID" API for the given Portal ID.
   * Sets the Carton to be validated by the Portal.
   */
  public enterCartonId(portalId: number, cartonId: string): Promise<Oneill_GetStateDto> {
    const url = `${Url_Base}/oneill/carton-portal-validation/${portalId}/enter-carton-id?carton_id=${cartonId}`;
    return this.restService.post<Oneill_GetStateDto>(url, null);
  }

  /**
   * Calls the "Process Carton" API for the given Portal ID.
   * Processes the current Carton Validation Attempt at the portal and returns the latest state.
   */
  public processCarton(portalId: number): Promise<Oneill_GetStateDto> {
    const url = `${Url_Base}/oneill/carton-portal-validation/${portalId}/process-carton`;
    return this.restService.post<Oneill_GetStateDto>(url, null);
  }

  /**
   * Calls the "Clear Carton" API for the given Portal ID.
   * Clears the current Carton Validation Attempt at the portal and returns the latest state.
   */
  public clearCarton(portalId: number): Promise<Oneill_GetStateDto> {
    const url = `${Url_Base}/oneill/carton-portal-validation/${portalId}/clear-carton`;
    return this.restService.post<Oneill_GetStateDto>(url, null);
  }

}

/**
 * Returns a boolean indicating if the given Portal is set to 
 * Autoprocess the results.
 */
export function isSetToAutoprocess(portal: ServiceInstance): boolean {
  const data = portal?.data?.find(d => d?.property?.property == Oneil_ServicePropertyKey_Autoprocess);
  if (data?.object_value == true) {
    return true;
  } else {
    return false;
  }
}


/**
 * DTO used to describe the current state of a Portal
 */
export interface Oneill_GetStateDto {
  state: Oneill_ValidationStateEnum;
  attempt: Oneill_CvAttemptDto;
}

/**
 * DTO used to describe the Carton Validation Attempt
 */
export interface Oneill_CvAttemptDto {
  carton_id: string;
  line_items: Oneill_CvAttemptLineItemDto[];
}

/**
 * DTO use dto describe the Line Item for a Carton Validation Attempt
 */
export interface Oneill_CvAttemptLineItemDto {
  item: string;
  upc: string;
  required_quantity: number;
  scanned_quantity: number;
}

/**
 * Possible States for O'Neill's Carton Validation process
 */
export enum Oneill_ValidationStateEnum {
  
  /**
   * Waiting on the Carton ID to be scanned so that the 
   * contents can be looked up
   */
  WaitingOnCartonScan = "WaitingOnCartonScan",

  /**
   * Carton Contents are being retrieved...
   */
  RetrievingCartonContents = "RetrievingCartonContents",

  /**
   * Carton Contents retrieved / displayed and reads are being
   * applied to it.  (Carton is being validated)
   */
  CartonBeingValidated = "CartonBeingValidated",

  /**
   * Carton Contents retrieved and reads might habe been applied
   * to it.  It is currently being sent to their side / saved on 
   * our side, any new reads are being ignored. 
   */
  CartonBeingProcessed = "CartonBeingProcessed"

}
